import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Home, Newspaper, Video, ShoppingBag, Mail, MessageSquare, Send, MapPin, Info, FileText, EyeOff, ShieldAlert, User as UserIcon, Radio, Crown } from 'lucide-react';
import HeadlineTicker from './HeadlineTicker';
import AdSlot from '../AdSlot';
import { useAuth } from '../../context/AuthContext';
import { useSiteFlags } from '../../hooks/useSiteFlags';
import { LOGO_WIDE, LOGO_SQUARE } from '../../utils/brand';
import { appendUtm } from '../../utils/utm';

const LOGO = LOGO_SQUARE;

const MENU = [
  { to: '/', label: 'Home', Icon: Home, testId: 'nav-home' },
  { to: '/subscribe', label: 'Subscribe · Sign Up', Icon: Send, testId: 'nav-subscribe', accent: true },
  { to: '/stories', label: 'Investigations', Icon: Newspaper, testId: 'nav-stories' },
  { to: '/experiences', label: 'The Madame Files', Icon: ShieldAlert, testId: 'nav-experiences', accent: true },
  { to: '/explicit', label: '18+ Videos', Icon: Video, testId: 'nav-explicit', accent: true },
  { to: '/the-rumour-file', label: 'Rumour File', Icon: EyeOff, testId: 'nav-wall' },
  { to: '/founders', label: 'The Founders', Icon: Crown, testId: 'nav-founders', gold: true },
  { to: '/ganglandcards', label: 'Merch', Icon: ShoppingBag, testId: 'nav-merch', badge: 'SOON' },
];

const SECONDARY = [
  { to: '/about', label: 'About', Icon: Info, testId: 'nav-about' },
  { to: '/press', label: 'Press Kit', Icon: FileText, testId: 'nav-press' },
];

const TopNav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, hasAccess, loading: authLoading } = useAuth();
  const { madameFilesEnabled } = useSiteFlags();
  const menu = madameFilesEnabled ? MENU : MENU.filter((m) => m.to !== '/experiences');

  // Compact bar gets a deeper backdrop once user scrolls past hero
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll while drawer open
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  // Esc closes drawer
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      {/* Spacer so content isn't hidden under the fixed bar. Includes the
          ticker row that lives inside the fixed header. */}
      <div className="h-[104px] md:h-[120px]" aria-hidden="true" />

      <header
        data-testid="top-bar"
        className={`fixed top-0 inset-x-0 z-40 transition-colors duration-200 ${
          scrolled
            ? 'bg-black/95 border-b border-zinc-800'
            : 'bg-black/85 border-b border-zinc-900'
        }`}
      >

        <div className="max-w-7xl mx-auto px-3 sm:px-5 h-16 sm:h-20 md:h-24 grid grid-cols-[auto_1fr_auto] items-center gap-2 sm:gap-6">
          {/* LEFT: Outlaw Media wordmark - first thing you see. Never scales
              below its natural size so the logo always shows. */}
          <Link
            to="/"
            data-testid="top-bar-logo"
            className="flex items-center group shrink-0"
          >
            <img
              src={LOGO_WIDE}
              alt="Outlaw Media"
              width="1504"
              height="229"
              fetchpriority="high"
              decoding="async"
              className="h-9 sm:h-10 md:h-12 w-auto max-w-[210px] sm:max-w-[260px] md:max-w-[320px] object-contain shrink-0 transition-opacity group-hover:opacity-90"
              draggable="false"
            />
          </Link>

          {/* CENTER: Reserved space (empty by design - trending chip
              removed per user request). Kept as a grid cell so the logo
              stays hard-left and the actions stay hard-right. */}
          <div className="min-w-0" data-testid="top-bar-desktop-nav-placeholder" aria-hidden="true" />

          {/* RIGHT: Sign-up CTA + account + hamburger (all breakpoints).
              Every button here shares the same h-11 height so the header
              row lines up cleanly on desktop. Gap widens on md+ to give
              each action breathing room. */}
          <div className="flex items-center justify-end gap-1.5 md:gap-3 shrink-0">
            {!authLoading && !user && (
              <>
                <Link
                  to={appendUtm("/login")}
                  data-testid="topnav-login"
                  aria-label="Log in"
                  className="inline-flex items-center h-11 rounded border border-zinc-700 hover:border-red-500 text-[11px] font-black uppercase tracking-[0.2em] text-gray-200 hover:text-white hover:bg-white/5 transition-colors whitespace-nowrap px-2.5 md:px-3 gap-1.5"
                >
                  <UserIcon className="h-3.5 w-3.5 sm:hidden" />
                  <span className="hidden sm:inline">Log in</span>
                </Link>
                <Link
                  to={appendUtm("/subscribe")}
                  data-testid="topnav-create-account"
                  className="inline-flex items-center h-11 rounded bg-red-600 hover:bg-red-500 text-white text-[11px] font-black tracking-[0.15em] uppercase transition-colors shadow-[0_0_15px_rgba(220,38,38,0.4)] whitespace-nowrap px-2.5 md:px-4"
                >
                  <span className="md:hidden">Subscribe</span>
                  <span className="hidden md:inline">Subscribe · Sign Up</span>
                </Link>
              </>
            )}
            {!authLoading && (
              <div className="hidden lg:flex items-center gap-2" data-testid="topnav-auth-cluster">
                {user && !hasAccess && (
                  <Link
                    to="/account"
                    data-testid="topnav-account"
                    className="inline-flex items-center h-11 px-3 rounded gap-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                  >
                    <UserIcon className="h-3.5 w-3.5" /> Account
                  </Link>
                )}
                {user && hasAccess && (
                  <>
                    <span
                      data-testid="topnav-access-badge"
                      className={`inline-flex items-center h-11 px-3 rounded text-[9px] font-black uppercase tracking-[0.3em] ${
                        user.plan === 'founding'
                          ? 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/50'
                          : 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/50'
                      }`}
                    >
                      {user.plan === 'founding' ? 'Founder' : 'Access'}
                    </span>
                    <Link
                      to="/account"
                      data-testid="topnav-account"
                      className="inline-flex items-center h-11 px-3 rounded gap-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <UserIcon className="h-3.5 w-3.5" /> Account
                    </Link>
                  </>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={() => {
                const btn = document.querySelector('[data-testid="submit-tip-button"]');
                if (btn) btn.click();
              }}
              data-testid="top-bar-tip"
              className="hidden md:inline-flex items-center h-11 px-3 md:px-4 rounded gap-1.5 bg-red-600 hover:bg-red-500 text-white text-[11px] font-bold tracking-wider uppercase transition-colors whitespace-nowrap"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Got a Tip?
            </button>
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="top-nav-toggle"
              aria-label="Open menu"
              className="inline-flex items-center h-11 rounded-md bg-red-600 hover:bg-red-500 border border-red-500 text-white transition-colors shadow-[0_0_18px_rgba(220,38,38,0.45)] px-3 md:px-5 gap-0 md:gap-2"
            >
              <Menu className="h-5 w-5" />
              <span className="hidden md:inline text-[11px] md:text-xs font-black tracking-[0.3em] uppercase">Menu</span>
            </button>
          </div>
        </div>

        {/* Breaking-news ticker sits inside the fixed header so it stays
            pinned at the top of every page while the user scrolls. */}
        <HeadlineTicker />
      </header>

      {/* Global top ad - appears in normal flow directly below the fixed
          header on every page. Renders as a slim leaderboard on mobile and
          a proper billboard aspect on desktop. */}
      <AdSlot
        slotId="global-top"
        label="Sponsored"
        className="pt-3 pb-4 border-b border-zinc-800"
        aspect="aspect-[5/1] md:aspect-[8/1]"
      />

      {/* Mobile drawer */}
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            data-testid="top-nav-backdrop"
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm animate-fade-in"
          />
          <aside
            data-testid="top-nav-drawer"
            className="fixed top-0 right-0 z-50 h-full w-full max-w-xs bg-zinc-950/95 backdrop-blur-2xl border-l border-red-900/40 shadow-[-12px_0_30px_rgba(0,0,0,0.6)] animate-slide-in-right flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-red-900/40">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-md overflow-hidden bg-black border border-red-700/40">
                  <img src={LOGO} alt="" className="w-full h-full object-contain p-0.5" />
                </div>
                <span className="text-xs font-black tracking-[0.25em] text-red-400 uppercase">
                  Outlaw Menu
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="top-nav-close"
                aria-label="Close navigation"
                className="w-9 h-9 rounded-full hover:bg-zinc-800 text-gray-300 hover:text-white flex items-center justify-center transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto py-3" data-testid="top-nav-list">
              {/* Account CTA for anon readers - top of drawer so it's the
                  first thing they see when the menu opens. */}
              {!user && (
                <div className="mx-5 mb-3 space-y-2">
                  <Link
                    to={appendUtm("/subscribe")}
                    onClick={() => setOpen(false)}
                    data-testid="drawer-create-account"
                    className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 text-white font-black text-xs uppercase tracking-[0.2em] px-4 py-3 rounded shadow-[0_0_20px_rgba(220,38,38,0.5)]"
                  >
                    Subscribe · Sign Up
                  </Link>
                  <Link
                    to={appendUtm("/login")}
                    onClick={() => setOpen(false)}
                    data-testid="drawer-login"
                    className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white font-black text-xs uppercase tracking-[0.2em] px-4 py-3 rounded"
                  >
                    <UserIcon className="h-4 w-4" />
                    Log in
                  </Link>
                  <p className="text-[10px] text-zinc-400 text-center leading-relaxed">
                    Already subscribed? Tap <span className="text-white font-black">Log in</span>.
                    New here? Tap <span className="text-red-400 font-black">Subscribe · Sign Up</span>.
                  </p>
                </div>
              )}
              {user && (
                <div className="mx-5 mb-3">
                  <Link
                    to="/account"
                    onClick={() => setOpen(false)}
                    data-testid="drawer-account"
                    className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white font-black text-xs uppercase tracking-[0.2em] px-4 py-3 rounded"
                  >
                    <UserIcon className="h-4 w-4" />
                    Your Account
                  </Link>
                </div>
              )}

              {menu.map(({ to, label, Icon, accent, gold, badge, testId }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setOpen(false)}
                  data-testid={`drawer-${testId}`}
                  className={`flex items-center gap-3 px-5 py-3.5 border-l-2 transition-all ${
                    gold
                      ? 'border-yellow-500 text-yellow-300 hover:bg-yellow-500/10'
                      : accent
                        ? 'border-red-500 text-red-400 hover:bg-red-600/10'
                        : 'border-transparent hover:border-red-500/50 hover:bg-red-600/5 text-gray-200 hover:text-white'
                  }`}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  <span className="font-black tracking-wide text-base">{label}</span>
                  {badge && (
                    <span className="ml-auto text-[9px] font-black tracking-widest bg-yellow-400/15 text-yellow-300 px-2 py-0.5 rounded-full border border-yellow-500/30">
                      {badge}
                    </span>
                  )}
                </Link>
              ))}

              <Link
                to="/ganglandmap"
                onClick={() => setOpen(false)}
                data-testid="drawer-nav-map"
                className="flex items-center gap-3 px-5 py-3.5 border-l-2 border-transparent hover:border-red-500/50 hover:bg-red-600/5 text-gray-200 hover:text-white transition-all"
              >
                <MapPin className="h-5 w-5 shrink-0" />
                <span className="font-black tracking-wide text-base">Gangland Map</span>
                <span className="ml-auto text-[9px] font-black tracking-widest bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full border border-red-500/40">
                  BETA
                </span>
              </Link>

              <div className="my-3 mx-5 border-t border-red-900/30"></div>
              {SECONDARY.map(({ to, label, Icon, testId }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setOpen(false)}
                  data-testid={`drawer-${testId}`}
                  className="flex items-center gap-3 px-5 py-2.5 text-gray-400 hover:text-red-400 transition-colors text-sm font-bold"
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </Link>
              ))}

              <div className="my-3 mx-5 border-t border-red-900/30"></div>

              <a
                href="mailto:ryan@outlawmedia.com.au"
                onClick={() => setOpen(false)}
                data-testid="drawer-contact"
                className="flex items-center gap-3 px-5 py-3 text-gray-400 hover:text-red-400 transition-colors text-sm font-bold"
              >
                <Mail className="h-4 w-4" />
                Contact
              </a>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setTimeout(() => {
                    const btn = document.querySelector('[data-testid="submit-tip-button"]');
                    if (btn) btn.click();
                  }, 250);
                }}
                data-testid="drawer-tip"
                className="w-full text-left flex items-center gap-3 px-5 py-3 text-gray-400 hover:text-red-400 transition-colors text-sm font-bold"
              >
                <MessageSquare className="h-4 w-4" />
                Submit a Tip
              </button>
            </nav>

            {/* Sponsored slots */}
            <a
              href="https://thesesh.tv"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="drawer-sesh-with-sav"
              className="relative block px-5 py-3 border-t border-orange-900/40 bg-gradient-to-b from-black/0 via-orange-500/5 to-orange-500/10 hover:to-orange-500/20 transition-colors group"
            >
              <div className="absolute inset-x-5 top-2 h-px bg-gradient-to-r from-transparent via-orange-400/50 to-transparent" aria-hidden="true" />
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 opacity-70 group-hover:opacity-100 transition-opacity"
                style={{ boxShadow: 'inset 0 0 24px rgba(249,115,22,0.18)' }}
              />
              <p className="text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-1.5">
                We Recommend
              </p>
              <div className="relative flex items-center justify-between gap-3">
                <img
                  src="https://customer-assets.emergentagent.com/job_app-showcase-210/artifacts/omgogjtk_1033.png"
                  alt="The Sesh with Sav"
                  className="h-7 w-auto mix-blend-screen group-hover:scale-105 transition-transform drop-shadow-[0_0_10px_rgba(249,115,22,0.6)]"
                />
                <span className="text-[10px] font-black tracking-widest text-orange-300 uppercase border border-orange-500/40 rounded px-2 py-1 group-hover:bg-orange-500/10 transition-colors">
                  Watch →
                </span>
              </div>
            </a>

            <p className="px-5 py-3 text-[10px] text-gray-600 border-t border-red-900/30 text-center">
              Outlaw Media · Dubai HQ · Melbourne Streets
            </p>
          </aside>
        </>
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-fade-in { animation: fade-in 0.2s ease-out; }
        .animate-slide-in-right { animation: slide-in-right 0.25s ease-out; }
      `}</style>
    </>
  );
};

export default TopNav;
